import { KeyboardEvent, useRef, useState } from 'react';
import { monthlySpendError } from './currentSupplierLimits';

const EMPTY_OVERRIDE = 'Enter the monthly spend to use instead.';

const draftError = (draft: string) => (draft.trim() === '' ? EMPTY_OVERRIDE : monthlySpendError(draft));

/**
 * The override being typed: saved on blur or Enter, dropped on Escape, and kept open when its save is refused
 * or when more was typed while it saved.
 * Left at the amount it started from, it closes unsaved, so the field keeps following Current Suppliers.
 */
export const useOverrideDraft = (onOverride: (monthlyAmount: number) => Promise<boolean>) => {
  const [draft, setDraft] = useState<string | null>(null);
  const [showError, setShowError] = useState(false);
  const saving = useRef(false);
  const startedFrom = useRef(0);
  // A blur as Escape unmounts the input still sees this render's draft, so Escape is remembered here.
  const cancelled = useRef(false);
  const error = draft === null ? null : draftError(draft);

  const start = (amount: number | null) => {
    cancelled.current = false;
    startedFrom.current = amount ?? 0;
    setDraft(String(startedFrom.current));
    setShowError(false);
  };

  const save = async () => {
    if (draft === null || saving.current || cancelled.current) return;
    if (error) {
      setShowError(true);
      return;
    }
    if (Number(draft) === startedFrom.current) {
      setDraft(null);
      return;
    }
    const sent = draft;
    saving.current = true;
    const saved = await onOverride(Number(sent));
    saving.current = false;
    if (!saved) return;
    startedFrom.current = Number(sent);
    setDraft((typed) => (typed === sent ? null : typed));
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter') save();
    if (event.key === 'Escape') {
      cancelled.current = true;
      setDraft(null);
    }
  };

  return { draft, error: showError ? error : null, start, change: setDraft, save, onKeyDown };
};
